import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { LIMITS, overLimit } from '../lib/limits';
import { CategoryField, RoleStageFields } from '../components/ProfileFields';
import AutoTextarea from '../components/AutoTextarea';
import MyFiles from '../components/MyFiles';
import PasswordSettings from '../components/PasswordSettings';
import NotificationSettings from '../components/NotificationSettings';
import Avatar from '../components/Avatar';
import MatchProfile from '../components/MatchProfile';
import BlockedPeople from '../components/BlockedPeople';
import Loader from '../components/Loader';
import {
  CountryInput, LanguagePicker, TimezoneSelect, countryFor, countryPatch,
} from '../components/PlaceFields';

/* Every field saves itself. There is no Save button because a Save button is
   how a member fills in the whole page, gets pulled away, and loses it.

   Layers 1 and 2 save a second after you stop typing, and on leaving the field.
   Layer 3 saves only on leaving the field: the matcher reads intent, and it
   should never read half a sentence. Chips and selects save on the click.

   Each field is its own request. When they went together, one headline that was
   too long rejected every other change on the page along with it. */

const PROFILE_LAYER = {
  name: 1, headline: 1, location: 1, timezone_label: 1, languages: 1, org_name: 1, org_url: 1,
  role_tags: 1, stage: 1, stages_served: 1, categories: 1,
  building: 2, working_through: 2,
};
const PROFILE_KEYS = Object.keys(PROFILE_LAYER);
const INTENT_KEYS = ['seeking', 'offering', 'says_yes_to', 'not_looking_for'];
const EMPTY_INTENT = { seeking: '', offering: '', not_looking_for: '', says_yes_to: '' };
const TYPING_PAUSE_MS = 1000;

function changed(current, saved, keys) {
  const out = {};
  keys.forEach((k) => {
    if (JSON.stringify(current[k] ?? '') !== JSON.stringify(saved[k] ?? '')) out[k] = current[k];
  });
  return out;
}

const STATUS_TEXT = { saving: 'Saving…', saved: 'Saved ✓', error: 'Not saved' };

function Section({ n, eyebrow, title, blurb, state, onRetry, children, id }) {
  return (
    <section className="pe-section" id={id}>
      <header>
        <span className="pe-num">{eyebrow || `Layer ${n}`}</span>
        {state && (
          <span className={`pe-status ${state}`} role="status">
            {STATUS_TEXT[state]}
            {state === 'error' && (
              <button className="linklike" onClick={onRetry}>retry</button>
            )}
          </span>
        )}
        <h2>{title}</h2>
        <p>{blurb}</p>
      </header>
      <div className="pe-body">{children}</div>
    </section>
  );
}

function Field({ label, hint, error, count, max, children }) {
  const showCount = max && count > max * 0.8;
  return (
    <div className={`pe-field${error ? ' has-error' : ''}`}>
      <label className="flabel">
        {label}
        {showCount && <span className={`pe-count${count > max ? ' over' : ''}`}>{count}/{max}</span>}
      </label>
      {children}
      {error && <p className="pe-field-error" role="alert">{error}</p>}
      {hint && <p className="pe-hint">{hint}</p>}
    </div>
  );
}

/* Your photo: shown on your posts, comments, introductions and profile. */
function PhotoField({ notify, onChanged }) {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  useEffect(() => { api.me().then(setMe).catch(() => {}); }, []);
  async function pick(file) {
    if (!file) return;
    setBusy(true);
    try {
      const r = await api.uploadAvatar(file);
      setMe((m) => ({ ...m, avatar_url: r.avatar_url }));
      onChanged?.(r.avatar_url);
      notify?.('Photo updated');
    } catch (e) { notify?.(e.message); } finally { setBusy(false); }
  }
  async function remove() {
    setBusy(true);
    try {
      await api.deleteAvatar();
      setMe((m) => ({ ...m, avatar_url: '' }));
      onChanged?.('');
      notify?.('Photo removed');
    } catch (e) { notify?.(e.message); } finally { setBusy(false); }
  }
  if (!me) return null;
  return (
    <div className="pe-photo">
      <Avatar person={me} className="pe-photo-av" />
      <div className="pe-photo-actions">
        <button type="button" className="btn ghost sm" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? 'Uploading…' : me.avatar_url ? 'Change photo' : 'Add a photo'}
        </button>
        {me.avatar_url && <button type="button" className="linklike" disabled={busy} onClick={remove}>Remove</button>}
        <span className="muted-sm">A clear photo of your face helps people recognise you. PNG, JPG or WebP; we crop it square.</span>
        <input ref={input} type="file" hidden accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; pick(f); }} />
      </div>
    </div>
  );
}

/* Whether other members can find you in People → Discover (and ask to connect). */
function DiscoverSwitch({ notify }) {
  const [on, setOn] = useState(null);
  useEffect(() => { api.me().then((m) => setOn(m.show_in_discover !== false)).catch(() => setOn(true)); }, []);
  if (on === null) return null;
  return (
    <label className="news-switch">
      <input type="checkbox" checked={on} onChange={async (e) => {
        const next = e.target.checked;
        try { await api.updatePrefs({ show_in_discover: next }); setOn(next); notify?.(next ? 'Members can find you in Discover' : 'Hidden from Discover'); }
        catch (err) { notify?.(err.message); }
      }} />
      <span><b>Show me in People → Discover</b><br /><span className="muted-sm">Lets members find you and ask to connect. Your connections always see you.</span></span>
    </label>
  );
}

/* News is opt-in: most builders get headlines elsewhere and found it distracting. */
function NewsSwitch({ notify, onChanged }) {
  const [on, setOn] = useState(null);
  useEffect(() => { api.me().then((m) => setOn(Boolean(m.show_news))).catch(() => setOn(false)); }, []);
  if (on === null) return null;
  return (
    <label className="news-switch">
      <input type="checkbox" checked={on} onChange={async (e) => {
        const next = e.target.checked;
        try {
          await api.updatePrefs({ show_news: next });
          setOn(next);
          onChanged?.(next);
          notify?.(next ? 'News is on — it’s in the menu' : 'News is off');
        } catch (err) { notify?.(err.message); }
      }} />
      <span><b>Show News</b><br /><span className="muted-sm">A curated Web3, AI and fintech news page in the menu. Off by default.</span></span>
    </label>
  );
}

export default function ProfileEditor({ notify, guardRef, focusRequest, onSaved, onShowNewsChanged, onAvatarChanged }) {
  const [profile, setProfile] = useState(null);
  const [intent, setIntent] = useState(EMPTY_INTENT);
  const [evidence, setEvidence] = useState([]);
  const [newEvidence, setNewEvidence] = useState({ kind: 'link', label: '', url: '' });
  const [controls, setControls] = useState(null);
  const [status, setStatus] = useState({});
  const [stale, setStale] = useState(false);
  const [error, setError] = useState('');
  // Per-field save failures, shown under the field they belong to.
  const [fieldErrors, setFieldErrors] = useState({});
  const [loadError, setLoadError] = useState('');

  // The latest values and the last values the server confirmed. Refs, because
  // a save fired from a timer or an unmount must see now, not the render it
  // was scheduled in.
  const profileRef = useRef(null);
  const intentRef = useRef(EMPTY_INTENT);
  const savedProfile = useRef({});
  const savedIntent = useRef(EMPTY_INTENT);
  const timer = useRef(null);
  const newEvidenceRef = useRef({ kind: 'link', label: '', url: '' });
  const addingEvidence = useRef(false);

  useEffect(() => {
    Promise.all([api.me(), api.controls()])
      .then(([me, c]) => {
        const loadedIntent = {
          seeking: me.intent?.seeking || '',
          offering: me.intent?.offering || '',
          not_looking_for: me.intent?.not_looking_for || '',
          says_yes_to: me.intent?.says_yes_to || '',
        };
        profileRef.current = me;
        savedProfile.current = me;
        intentRef.current = loadedIntent;
        savedIntent.current = loadedIntent;
        setProfile(me);
        setIntent(loadedIntent);
        setStale(Boolean(me.intent?.is_stale));
        setEvidence(me.evidence || []);
        setControls(c);
      })
      .catch((e) => setLoadError(e.message));
  }, []);

  const mark = useCallback((layers, value) => {
    setStatus((s) => {
      const next = { ...s };
      layers.forEach((l) => { next[l] = value; });
      return next;
    });
  }, []);

  const setFieldError = useCallback((key, message) => {
    setFieldErrors((e) => {
      const next = { ...e };
      if (message) next[key] = message; else delete next[key];
      return next;
    });
  }, []);

  const flushProfile = useCallback(async () => {
    clearTimeout(timer.current);
    timer.current = null;
    if (!profileRef.current) return;
    const changes = changed(profileRef.current, savedProfile.current, PROFILE_KEYS);
    if ('name' in changes && !(changes.name || '').trim()) delete changes.name;
    // Too long is shown under the field as you type; sending it would only fail.
    const keys = Object.keys(changes).filter((k) => !overLimit(k, changes[k]));
    if (keys.length === 0) return;

    const layers = [...new Set(keys.map((k) => PROFILE_LAYER[k]))];
    mark(layers, 'saving');
    const failed = [];
    await Promise.all(keys.map(async (key) => {
      const before = savedProfile.current[key];
      // Recorded as saved before the request lands, so a second flush while
      // this one is in flight does not send the same change twice.
      savedProfile.current = { ...savedProfile.current, [key]: changes[key] };
      try {
        await api.updateMe({ [key]: changes[key] });
        setFieldError(key, '');
      } catch (e) {
        savedProfile.current = { ...savedProfile.current, [key]: before };
        setFieldError(key, `Not saved: ${e.message}`);
        failed.push(key);
      }
    }));
    layers.forEach((layer) => {
      mark([layer], failed.some((k) => PROFILE_LAYER[k] === layer) ? 'error' : 'saved');
    });
    if (failed.length < keys.length) onSaved?.();
  }, [mark, setFieldError, onSaved]);

  const flushIntent = useCallback(async () => {
    const changes = changed(intentRef.current, savedIntent.current, INTENT_KEYS);
    const keys = Object.keys(changes);
    if (keys.length === 0) return;

    mark([3], 'saving');
    const failed = [];
    await Promise.all(keys.map(async (key) => {
      const before = savedIntent.current[key];
      savedIntent.current = { ...savedIntent.current, [key]: changes[key] };
      try {
        await api.updateIntent({ [key]: changes[key] });
        setFieldError(key, '');
      } catch (e) {
        savedIntent.current = { ...savedIntent.current, [key]: before };
        setFieldError(key, `Not saved: ${e.message}`);
        failed.push(key);
      }
    }));
    mark([3], failed.length ? 'error' : 'saved');
    if (failed.length < keys.length) {
      setStale(false);
      onSaved?.();
    }
  }, [mark, setFieldError, onSaved]);

  // A half-typed evidence row is saved rather than dropped. It needs a label;
  // a link on its own is not a claim.
  const flushEvidence = useCallback(async () => {
    const row = newEvidenceRef.current;
    if (!row.label.trim() || addingEvidence.current) return;
    addingEvidence.current = true;
    const url = row.url.trim();
    try {
      const saved = await api.addEvidence({
        kind: url ? 'link' : 'experience',
        label: row.label.trim(),
        url,
        keywords: [],
      });
      setEvidence((list) => [...list, saved]);
      newEvidenceRef.current = { kind: 'link', label: '', url: '' };
      setNewEvidence(newEvidenceRef.current);
      setError('');
      onSaved?.();
    } catch (e) {
      setError(`Couldn't add that evidence: ${e.message}`);
    } finally {
      addingEvidence.current = false;
    }
  }, [onSaved]);

  const flushAll = useCallback(
    () => Promise.all([flushProfile(), flushIntent(), flushEvidence()]),
    [flushProfile, flushIntent, flushEvidence]
  );

  const hasUnsaved = useCallback(() => {
    if (!profileRef.current) return false;
    return (
      timer.current !== null
      || Object.keys(changed(profileRef.current, savedProfile.current, PROFILE_KEYS)).length > 0
      || Object.keys(changed(intentRef.current, savedIntent.current, INTENT_KEYS)).length > 0
      || newEvidenceRef.current.label.trim().length > 0
    );
  }, []);

  // Lets the shell save before navigating away, and ask if a save failed.
  useEffect(() => {
    if (guardRef) guardRef.current = { flush: flushAll, hasUnsaved };
    const onUnload = (e) => {
      if (!hasUnsaved()) return;
      flushAll();
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      flushAll();
      if (guardRef) guardRef.current = null;
    };
  }, [guardRef, flushAll, hasUnsaved]);

  // "Add it" from the profile meter lands on the field itself, not the top of
  // the page.
  const loaded = profile !== null;
  useEffect(() => {
    if (!focusRequest || !loaded) return undefined;
    const el = document.getElementById(`pf-${focusRequest.key}`);
    if (!el) return undefined;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const target = el.matches('input, textarea') ? el : el.querySelector('input, textarea, button');
    target?.focus({ preventScroll: true });
    const box = el.closest('.pe-field') || el;
    box.classList.add('pe-flash');
    const t = setTimeout(() => box.classList.remove('pe-flash'), 2400);
    return () => clearTimeout(t);
  }, [focusRequest, loaded]);

  if (loadError) return <div className="pe-error">{loadError}</div>;
  if (!profile) return <Loader label="Loading your profile…" />;

  function editProfile(patch, { now = false } = {}) {
    const next = { ...profileRef.current, ...patch };
    profileRef.current = next;
    setProfile(next);
    clearTimeout(timer.current);
    if (now) {
      flushProfile();
    } else {
      timer.current = setTimeout(flushProfile, TYPING_PAUSE_MS);
    }
  }

  function editIntent(key, value) {
    const next = { ...intentRef.current, [key]: value };
    intentRef.current = next;
    setIntent(next);
  }

  // Text fields: save on a pause and on leaving the field.
  const text = (key) => ({
    id: `pf-${key}`,
    value: profile[key] || '',
    onChange: (e) => editProfile({ [key]: e.target.value }),
    onBlur: flushProfile,
  });
  const intentText = (key) => ({
    id: `pf-${key}`,
    value: intent[key],
    onChange: (e) => editIntent(key, e.target.value),
    onBlur: flushIntent,
  });
  // Error, character count and limit for a Layer 1 field.
  const check = (key) => {
    const value = profile[key] || '';
    const tooLong = overLimit(key, value);
    return {
      error: tooLong
        ? `Too long to save — ${value.length} of ${LIMITS[key]} characters. Shorten it and it saves.`
        : fieldErrors[key],
      count: value.length,
      max: LIMITS[key],
    };
  };
  // A layer shows "Not saved" while any of its fields cannot be saved.
  const layerState = (n) => {
    const blocked = PROFILE_KEYS.some((k) => PROFILE_LAYER[k] === n
      && (overLimit(k, profile[k]) || fieldErrors[k]));
    const intentBlocked = n === 3 && INTENT_KEYS.some((k) => fieldErrors[k]);
    return blocked || intentBlocked ? 'error' : status[n];
  };
  const pick = (patch) => editProfile(patch, { now: true });
  const retry = () => flushAll();

  const country = countryFor(profile.location);

  function editEvidence(patch) {
    newEvidenceRef.current = { ...newEvidenceRef.current, ...patch };
    setNewEvidence(newEvidenceRef.current);
  }

  // Leaving the row (not moving between its own inputs) saves it.
  function onEvidenceBlur(e) {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    flushEvidence();
  }

  async function removeEvidence(id) {
    try {
      await api.deleteEvidence(id);
      setEvidence(evidence.filter((x) => x.id !== id));
    } catch (e) { notify(e.message); }
  }

  return (
    <div className="pe">
      <div className="pe-head">
        <div>
          <h1>{profile.name || 'Your profile'}</h1>
          <p className="pe-handle">
            Your handle <code>@{profile.handle}</code> · changes save as you go
          </p>
        </div>
      </div>

      {error && (
        <div className="pe-error">
          {error}{' '}
          <button className="linklike" onClick={retry}>Try again</button>
        </div>
      )}
      {stale && (
        <div className="pe-nudge">
          Your intent hasn't been confirmed recently. Stale intent produces confident wrong
          matches — worse than leaving it empty.{' '}
          <button
            className="linklike"
            onClick={() => api.confirmIntent().then(() => { setStale(false); notify('Confirmed as current'); }).catch((e) => notify(e.message))}
          >
            Still accurate
          </button>
        </div>
      )}

      <Section n={1} title="Identity" blurb="Who you are and where. Public." state={layerState(1)} onRetry={retry}>
        <PhotoField notify={notify} onChanged={onAvatarChanged} />
        <div className="pe-grid">
          <Field label="Name" {...check('name')}><input className="in" {...text('name')} /></Field>
          <Field label="Headline" {...check('headline')} hint="One line — what you do and for whom. The longer story goes in Layer 2.">
            <input className="in" {...text('headline')} placeholder="e.g. Fractional COO & GTM advisor" />
          </Field>
          <Field label="Country" {...check('location')}>
            <CountryInput
              id="pf-location"
              value={profile.location}
              placeholder="Search your country"
              onPick={(c) => pick(countryPatch(c, profile.timezone_label))}
            />
          </Field>
          <Field
            label="Timezone"
            {...check('timezone_label')}
            hint={country && country.offsets.length > 1 && !profile.timezone_label
              ? `${country.name} has several timezones — pick yours.`
              : null}
          >
            <TimezoneSelect
              id="pf-timezone_label"
              value={profile.timezone_label}
              country={country}
              onChange={(v) => pick({ timezone_label: v })}
            />
          </Field>
          <Field label="Languages" {...check('languages')}>
            <LanguagePicker
              id="pf-languages"
              value={profile.languages}
              onChange={(v) => pick({ languages: v })}
            />
          </Field>
          <Field label="Organisation" {...check('org_name')}><input className="in" {...text('org_name')} /></Field>
          <Field label="Organisation URL" {...check('org_url')}><input className="in" {...text('org_url')} placeholder="https://" /></Field>
        </div>
        <RoleStageFields profile={profile} onChange={pick} anchors />
        <CategoryField profile={profile} onChange={pick} anchors />
      </Section>

      <Section
        n={2}
        title="What you're doing now"
        blurb="About you and your own business or project — not your clients'. Public, and you'll be nudged to refresh it now and then."
        state={layerState(2)}
        onRetry={retry}
      >
        <Field label="What I'm building" error={fieldErrors.building} hint="Your company or project, its stage, and the problem it solves. A paragraph is plenty.">
          <AutoTextarea className="ta" {...text('building')} />
        </Field>
        <Field label="What I'm working through right now" error={fieldErrors.working_through} hint="The live problem in your own business. This is what makes a match timely rather than generic.">
          <AutoTextarea className="ta" {...text('working_through')} />
        </Field>
      </Section>

      <Section
        n={3}
        title="Intent — the matching layer"
        blurb="Members only unless you opt a field public. This is what the matcher actually reads. Each box saves when you click out of it. Our AI reads it on our own servers to find introductions — it is never sold or shared."
        state={layerState(3)}
        onRetry={retry}
      >
        <Field label="Seeking" error={fieldErrors.seeking} hint="Who you want to meet through this network — clients for your business count. Describe the person, not a category.">
          <AutoTextarea className="ta" {...intentText('seeking')}
            placeholder="e.g. Web3 founders with an MVP and budget for go-to-market help" />
        </Field>
        <Field label="Offering" error={fieldErrors.offering} hint="What you bring — including what you do commercially. Everyone here is somebody's supplier and somebody's customer; these two boxes are those two directions.">
          <AutoTextarea className="ta" {...intentText('offering')} />
        </Field>
        <Field
          label="A conversation I'd say yes to right now"
          error={fieldErrors.says_yes_to}
          hint="One specific conversation, not a type of client. The matcher leans on this more than anything else — the more concrete it is, the better the introduction."
        >
          <AutoTextarea className="ta" {...intentText('says_yes_to')}
            placeholder="e.g. 30 minutes with a Web3 founder whose MVP has users but no paying customers yet." />
        </Field>
        <Field label="Not looking for" error={fieldErrors.not_looking_for} hint="Prevents more bad matches than any positive field creates good ones.">
          <AutoTextarea className="ta" {...intentText('not_looking_for')}
            placeholder="e.g. Full-time roles · outbound agency pitches" />
        </Field>
      </Section>

      <Section
        id="pf-match-section"
        eyebrow="Layer 3 · Match profile"
        title="What the matcher uses"
        blurb="The facts introductions are built on: what you need, what you give, and who you want to meet. Copilot fills it from your profile — check it and confirm."
      >
        <MatchProfile notify={notify} />
      </Section>

      <Section n={4} title="Evidence" blurb="Public. What makes the rest credible.">
        <div className="pe-evidence">
          {evidence.map((row) => (
            <div className="pe-ev" key={row.id}>
              <span className="chip offer">{row.kind}</span>
              <span className="pe-ev-label">{row.label}</span>
              {row.url && <a href={row.url} target="_blank" rel="noreferrer">{row.url.slice(0, 40)}</a>}
              <button className="linklike danger" onClick={() => removeEvidence(row.id)}>remove</button>
            </div>
          ))}
          {evidence.length === 0 && <p className="pe-hint">Nothing yet — add a claim, with a link if you have one.</p>}
        </div>
        <div className="pe-addrow" onBlur={onEvidenceBlur}>
          <input className="in" placeholder="e.g. 15 yrs in consulting"
            value={newEvidence.label}
            onChange={(e) => editEvidence({ label: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') flushEvidence(); }} />
          <input className="in" placeholder="Link that backs it up (optional)"
            value={newEvidence.url}
            onChange={(e) => editEvidence({ url: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') flushEvidence(); }} />
          <button className="btn ghost" onClick={flushEvidence} disabled={!newEvidence.label.trim()}>Add</button>
        </div>
        <p className="pe-hint">Adds itself when you click away. A LinkedIn profile or company page is plenty — no GitHub needed.</p>
      </Section>

      <Section
        id="pf-files-section"
        eyebrow="Layer 4 · Files"
        title="Resume & portfolio"
        blurb="Upload your resume, portfolio or a one-pager. Private unless you share it — the matcher reads it so the right people can find you."
      >
        <MyFiles notify={notify} onChanged={onSaved} />
      </Section>

      {controls && (
        <Section n={5} title="Your controls" blurb="You own the tap. These throttle the matcher when it arrives.">
          <div className="pe-grid">
            <Field label="Availability" hint="Heads-down stops proposals without you having to decline them.">
              <select className="in" value={controls.availability}
                onChange={(e) => {
                  const v = e.target.value;
                  setControls({ ...controls, availability: v });
                  api.updateControls({ availability: v }).then(() => notify('Saved')).catch((e) => notify(e.message));
                }}>
                <option value="open">open to connect</option>
                <option value="heads_down">heads-down</option>
                <option value="not_now">not now</option>
              </select>
            </Field>
            <Field label="Proposals per week">
              <input className="in" type="number" min="0" max="20" value={controls.proposals_per_week}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setControls({ ...controls, proposals_per_week: v });
                  if (v >= 0 && v <= 20) api.updateControls({ proposals_per_week: v }).catch((err) => notify(err.message));
                }} />
            </Field>
            {/* Radius is stored but nothing reads it yet, so it is not offered —
                a setting that changes nothing is worse than no setting. */}
            <Field label="Exploration %" hint="Share of proposals deliberately outside your learned taste. Zero means you only ever meet people like the ones you already liked.">
              <input className="in" type="number" min="0" max="100" value={controls.exploration_rate}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setControls({ ...controls, exploration_rate: v });
                  if (v >= 0 && v <= 100) api.updateControls({ exploration_rate: v }).catch((err) => notify(err.message));
                }} />
            </Field>
          </div>
        </Section>
      )}

      <Section
        id="pf-notifications-section"
        eyebrow="Account"
        title="Email notifications"
        blurb="So you don't miss an introduction or a reply. Choose which emails you get."
      >
        <NotificationSettings notify={notify} />
      </Section>

      <Section
        id="pf-display-section"
        eyebrow="Account"
        title="What you see"
        blurb="Turn parts of Builders' Network on or off."
      >
        <NewsSwitch notify={notify} onChanged={onShowNewsChanged} />
        <DiscoverSwitch notify={notify} />
      </Section>

      <Section
        id="pf-blocked-section"
        eyebrow="Account"
        title="Blocked people"
        blurb="People you've blocked can't message you, aren't introduced to you, and you don't see their posts. They aren't told."
      >
        <BlockedPeople notify={notify} />
      </Section>

      <Section
        id="pf-password-section"
        eyebrow="Account"
        title="Password"
        blurb="Sign in with your email and password as well as (or instead of) Google. Got a temporary password from an admin? Change it here."
      >
        <PasswordSettings notify={notify} />
      </Section>
    </div>
  );
}
